import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import type { CurrentRain, RainDetail } from '../services/generated/Api';

export const RAIN_HISTORY_PAGE_SIZE = 20;

export function useCurrentRain(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rainCurrent,
    queryFn: async () => {
      const response = await apiClient.api.rainsControllerGetCurrent();
      return response.rain ?? null;
    },
    enabled,
    staleTime: 30 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });
}

export function useLastRain(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rainLast,
    queryFn: async () => {
      const res = await apiClient.api.rainsControllerGetHistory({ page: 1, limit: 1 });
      return res.rains[0] ?? null;
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useRainHistory(enabled = true) {
  const apiClient = useApiClient();

  return useInfiniteQuery({
    queryKey: queryKeys.rainHistory,
    queryFn: async ({ pageParam = 1 }) => {
      const res = await apiClient.api.rainsControllerGetHistory({
        page: pageParam,
        limit: RAIN_HISTORY_PAGE_SIZE,
      });
      return { rains: res.rains, hasMore: res.hasMore, page: pageParam };
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    initialPageParam: 1,
    enabled,
    staleTime: 60 * 1000,
  });
}

export const RAIN_PARTICIPANTS_PAGE_SIZE = 20;

export function useRainDetails(rainId: string, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.rainDetail(rainId),
    queryFn: async () => {
      const response = await apiClient.api.rainsControllerGetById(rainId);
      return response.rain ?? null;
    },
    enabled: enabled && !!rainId,
    staleTime: 15 * 1000,
  });
}

export function useRainParticipants(rainId: string, enabled = true) {
  const apiClient = useApiClient();

  return useInfiniteQuery({
    queryKey: queryKeys.rainParticipants(rainId),
    queryFn: async ({ pageParam = 1 }) => {
      const res = await apiClient.api.rainsControllerGetParticipants({
        id: rainId,
        page: pageParam,
        limit: RAIN_PARTICIPANTS_PAGE_SIZE,
      });
      return { participants: res.participants, total: res.total, hasMore: res.hasMore, page: pageParam };
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    initialPageParam: 1,
    enabled: enabled && !!rainId,
    staleTime: 15 * 1000,
  });
}

export function useJoinRain() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rainId: string) => apiClient.api.rainsControllerJoin(rainId),
    onSuccess: (_data, rainId) => {
      queryClient.setQueryData<CurrentRain | null>(queryKeys.rainCurrent, (prev) =>
        prev && prev.id === rainId
          ? { ...prev, isParticipating: true, participantsCount: prev.participantsCount + 1 }
          : prev,
      );
      queryClient.setQueryData<RainDetail | null>(queryKeys.rainDetail(rainId), (prev) =>
        prev ? { ...prev, isParticipating: true, participantsCount: prev.participantsCount + 1 } : prev,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.rainParticipants(rainId) });
    },
  });
}
